import type React from 'react';
// eslint-disable-next-line no-duplicate-imports
import { Box, Button, CloseButton, Flex, Textarea, useBreakpointValue } from '@chakra-ui/react';
import { ChatPrivilege } from '@zoom/videosdk';
import { Input } from 'antd';
import classnames from 'classnames';
import { ElementRef, FC, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IoIosSend } from 'react-icons/io';
import ZoomContext from '../../context/zoom-context';
import useLanguageSelector from '../../hooks/useLanguageSelector';
import useLessonContext from '../../hooks/useLessonContext';
import useUserContext from '../../hooks/useUserContext';
import { ChatService } from '../../service/chat-service';
import { isMobile } from '../../utils/DeviceType';
import { ChatContext } from '../video/context/chat-context';
import './chat.scss';
import ChatMessageItem from './component/chat-message-item';
import ChatReceiverContainer from './component/chat-receiver';

const { TextArea } = Input;
type Props = {
  onCloseChatView: () => void;
};
const defaultChatRows = 2;
const maxChatRows = 5;
const ChatContainer: FC<Props> = (props: Props) => {
  const LANG = useLanguageSelector();

  const { onCloseChatView } = props;
  const zmClient = useContext(ZoomContext);
  const chatClient = zmClient.getChatClient();
  const [chatDraft, setChatDraft] = useState<string>('');
  const chatWrapRef = useRef<HTMLDivElement | null>(null);
  const chatService = useMemo(() => new ChatService(), []);
  const [chatRows, setChatRows] = useState<number>(defaultChatRows);
  const {
    records,
    receivers,
    privilege,
    chatUser,
    isHostOrManager,
    setChatUserId,
    sendMessage,
    sendFile,
    resendFile,
    downloadFile
  } = useContext(ChatContext);
  const userCtx = useUserContext();
  const lessonCtx = useLessonContext();
  // const {
  //   records,
  //   receivers,
  //   privilege,
  //   chatUser,
  //   isHostOrManager,
  //   setChatUserId,
  //   sendMessage,
  //   sendFile,
  //   resendFile,
  //   downloadFile
  // } = useChat(zmClient, chatClient);
  const btnRef = useRef<ElementRef<'button'>>(null);
  const textareaRef = useRef<ElementRef<'textarea'>>(null);

  const onChatInput = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    // 文頭からの改行禁止、Enter送信後に改行だけ残るのを防止
    if (event.target.value !== '\n') {
      setChatDraft(event.target.value);
    }
  }, []);
  const onChatPressEnter = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      event.preventDefault();
      // if (chatUser && chatDraft) {
      //   sendMessage(chatDraft);
      //   setChatDraft('');
      // }
    },
    [sendMessage, chatUser, chatDraft]
  );
  const onSendClick = useCallback(() => {
    if (chatUser && chatDraft) {
      sendMessage(chatDraft);
      chatService.saveOnServer({
        companyId: lessonCtx.companyId,
        lessonId: lessonCtx.lessonId,
        message: chatDraft,
        receiverId: '',
        senderId: userCtx.userId
      });
      setChatDraft('');
    }
  }, [chatUser, chatDraft, sendMessage, setChatDraft]);
  const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (event) => {
    console.log(event.shiftKey && event.key === 'Enter');
    if (event.shiftKey && event.key === 'Enter') {
      // onChatInput();
      setChatRows((prev) => {
        if (prev < maxChatRows) {
          prev++;
        }
        return prev;
      });
    } else if (!event.shiftKey && event.key === 'Enter') {
      btnRef?.current?.click();
    }
  };
  useLayoutEffect(() => {
    if (chatWrapRef.current) {
      chatWrapRef.current.scrollTo(0, chatWrapRef.current.scrollHeight);
    }
  }, [records]);

  const iconStyles = useBreakpointValue({
    base: { width: '25px', height: '25px', color: 'blue' },
    lg: { width: '40px', height: '40px', color: 'blue' }
  });
  return isMobile() ? (
    <>
      <Box width={'100vw'}>
        <Flex
          className={classnames('chat-wrapper-mobile')}
          position={'absolute'}
          zIndex={1000}
          bottom={0}
          width={'100v'}
          height={'0'}
          alignItems={'center'}
          justifyContent={'center'}
        >
          <Flex
            className={classnames('chat-wrapper-mobile')}
            height={'0'}
            width={'100vw'}
            background={'#fff'}
            flexDirection={'column'}
            padding={'10px 0 160px'}
            position={'relative'}
          >
            <Flex alignItems={'center'} justifyContent={'center'} position={'relative'}>
              <h2>
                {/* Chat */}
                {LANG.KT_0100_15}
              </h2>
              <CloseButton position={'absolute'} right={0} onClick={onCloseChatView} />
            </Flex>
            <Box className="chat-message-wrap" ref={chatWrapRef} overflow={'auto'}>
              {records.map((record) => (
                <ChatMessageItem
                  record={record}
                  currentUserId={zmClient.getSessionInfo().userId}
                  setChatUser={setChatUserId}
                  key={record.timestamp}
                  resendFile={resendFile}
                  downloadFile={downloadFile}
                />
              ))}
            </Box>
            {ChatPrivilege.NoOne !== privilege || isHostOrManager ? (
              <>
                <Box
                  position={'absolute'}
                  width={'100%'}
                  // height={'15vh'}
                  maxHeight={'160px'}
                  bottom={'5px'}
                  padding={'0 5px 0'}
                >
                  <ChatReceiverContainer
                    chatUsers={receivers}
                    selectedChatUser={chatUser}
                    isHostOrManager={isHostOrManager}
                    chatPrivilege={privilege}
                    setChatUser={setChatUserId}
                    sendFile={sendFile}
                  />
                  <Flex flexDirection={'column'} alignItems={'center'} justifyContent={'center'}>
                    <Box className="chat-message-box" width={'100%'} alignItems={'center'}>
                      <Textarea
                        // onPressEnter={onChatPressEnter}
                        onChange={onChatInput}
                        value={chatDraft}
                        placeholder={LANG.KT_0100_19}
                        rows={chatRows}
                        ref={textareaRef}
                        resize={'none'}
                        onKeyDown={handleKeyDown}
                        fontSize={'16px'}
                      />
                    </Box>
                    <Button
                      ref={btnRef}
                      disabled={chatDraft.trim().length === 0}
                      onClick={onSendClick}
                      variant={'outline'}
                      color={'gray.500'}
                      p={0}
                      w={'50%'}
                      size={'sm'}
                    >
                      <IoIosSend style={iconStyles} />
                    </Button>
                  </Flex>
                </Box>
              </>
            ) : (
              <div className="chat-disabled">Chat disabled</div>
            )}
          </Flex>
        </Flex>
      </Box>
    </>
  ) : (
    <>
      <Box width={0} height={'100vh'}></Box> {/** ダミー */}
      <Flex
        className={classnames('chat-wrapper')}
        position={'absolute'}
        zIndex={1000}
        top={0}
        right={0}
        width={'0'}
        // className="chat-container"
        height={'100vh'}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <Flex
          // className="chat-wrap"
          height={'100vh'}
          width={'100%'}
          background={'#fff'}
          flexDirection={'column'}
          padding={'20px 0 160px'}
          position={'relative'}
        >
          <Flex alignItems={'center'} justifyContent={'center'} position={'relative'}>
            <h2>{LANG.KT_0100_15}</h2>
            <CloseButton position={'absolute'} right={0} onClick={onCloseChatView} />
          </Flex>
          <Box className="chat-message-wrap" ref={chatWrapRef} overflow={'auto'}>
            {records.map((record) => (
              <ChatMessageItem
                record={record}
                currentUserId={zmClient.getSessionInfo().userId}
                setChatUser={setChatUserId}
                key={record.timestamp}
                resendFile={resendFile}
                downloadFile={downloadFile}
              />
            ))}
          </Box>
          {ChatPrivilege.NoOne !== privilege || isHostOrManager ? (
            <>
              <Box
                position={'absolute'}
                width={'100%'}
                // height={'20vh'}
                maxHeight={'160px'}
                bottom={'5px'}
                padding={'0 5px 0'}
              >
                <ChatReceiverContainer
                  chatUsers={receivers}
                  selectedChatUser={chatUser}
                  isHostOrManager={isHostOrManager}
                  chatPrivilege={privilege}
                  setChatUser={setChatUserId}
                  sendFile={sendFile}
                />
                <Flex>
                  <Box className="chat-message-box" width={'100%'} alignItems={'center'}>
                    <Textarea
                      // onPressEnter={onChatPressEnter}
                      onChange={onChatInput}
                      value={chatDraft}
                      placeholder={LANG.KT_0100_19}
                      rows={chatRows}
                      ref={textareaRef}
                      resize={'none'}
                      onKeyDown={handleKeyDown}
                      fontSize={'16px'}
                    />
                  </Box>
                  <Button
                    ref={btnRef}
                    disabled={chatDraft.trim().length === 0}
                    onClick={onSendClick}
                    variant={'outline'}
                    color={'gray.500'}
                    p={0}
                    w={'10%'}
                    size={'sm'}
                  >
                    <IoIosSend style={iconStyles} />
                  </Button>
                </Flex>
              </Box>
            </>
          ) : (
            <div className="chat-disabled">Chat disabled</div>
          )}
        </Flex>
      </Flex>
    </>
  );
};

export default ChatContainer;
